import { FC, useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { DateTime } from "luxon";
import {
  fetchAdminRouteV2ControllerGetRoutes,
  useAdminRouteV2ControllerDeleteVirtualRoute,
  useAdminRouteV2ControllerTransferLocationToRoute,
} from "service";
import { useEstabrookContext } from "service/api/estabrookContext";
import { ControlledSelect, generateRoute, makeSxStyles } from "ui";
import { RouteStatus } from "../helpers/route-status.enum";
import DashboardContentLayout from "../../components/dashboard-content-layout";
import Navbar from "@/components/navbar/navbar";
import HeaderItems, {
  HeaderItemsLeft,
  HeaderItemsRight,
} from "@/shared/components/header-items/HeaderItems";
import { AdRoutes } from "@/constants/ad-routes";
import useCreateTempRouteStore from "@/state/create-temp-route-store";
import useRoutePlannerStore from "@/state/route-planner-store";
import { queryClient } from "@/query-client";
import { createRoutesInvalidationPredicate } from "service/api/estabrookQueryKeys";
import CustomColorSelector from "@/components/color-selector/color-selector";
import CreateTempRouteWorkflowStepper from "./create-temp-route-workflow-stepper";
import CreateTempRouteWarningBanner from "./create-temp-route-warning-banner";
import useVirtualRouteForm from "../use-virtual-route-form";

const CreateTempRouteDetailsPage: FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { date } = useParams<{ date: string }>();
  const { date: storedDate, candidates, reset } = useCreateTempRouteStore();
  const [isFinishing, setIsFinishing] = useState(false);

  // Live date, tracked in case it changes (e.g. End Shift) while this flow is open.
  const liveRoutePlannerDate = useRoutePlannerStore((state) => state.date);
  const { fetcherOptions } = useEstabrookContext();

  // Inherit the day's prevailing Start Time instead of defaulting to "now".
  const routesForDate = useRoutePlannerStore((state) => state.routes);
  const rawInheritedStartTime = routesForDate.find(
    (route) => !route.isVirtual && route.startTime,
  )?.startTime;

  // Fallback must be a future slot: the Start Time dropdown only has
  // hour/half-hour options and disables anything <= the current time.
  const nextFullHourStartTime = DateTime.now()
    .startOf("hour")
    .plus({ hours: 1 })
    .toFormat("HH:mm");

  const inheritedStartTime = (() => {
    if (!rawInheritedStartTime) return nextFullHourStartTime;
    const candidate = DateTime.fromFormat(
      `${storedDate} ${rawInheritedStartTime}`,
      "yyyy-MM-dd HH:mm",
    );
    return candidate.isValid && candidate > DateTime.now()
      ? rawInheritedStartTime
      : nextFullHourStartTime;
  })();

  // Candidates only live in the in-memory store - if missing (direct nav,
  // refresh), bounce back rather than let the user submit an empty route.
  useEffect(() => {
    if (!storedDate || candidates.length === 0) {
      navigate(generateRoute(AdRoutes.routePlanner, { date: date || "" }), {
        replace: true,
      });
    }
  }, [storedDate, candidates.length, navigate, date]);

  const { mutate: transferLocations } =
    useAdminRouteV2ControllerTransferLocationToRoute();
  const { mutate: deleteVirtualRoute } =
    useAdminRouteV2ControllerDeleteVirtualRoute();

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    setFieldTouched,
    errors,
    touched,
    driverOptions,
    isCreatingRoute,
  } = useVirtualRouteForm({
    date: storedDate,
    startTime: inheritedStartTime,
    // New temp routes are put first in the route list.
    routePriority: -1,
    // Reset isFinishing so the button doesn't stay stuck disabled on failure.
    onError: () => setIsFinishing(false),
    onSuccess: (route) => {
      if (!route?.id) return;

      const sequencedLocations = [...candidates]
        .filter(
          (address) =>
            address.locationSequence !== null &&
            address.locationSequence !== undefined,
        )
        .sort(
          (a, b) =>
            (a.locationSequence as number) - (b.locationSequence as number),
        );

      transferLocations(
        {
          pathParams: { date: storedDate },
          body: {
            targetDate: storedDate,
            targetRouteId: route.id,
            targetSeqNumber: 1,
            targetRouteOrderStatus: "regular",
            transferMode: "after",
            locationIds: sequencedLocations.map((location, index) => ({
              locationId: location.id,
              seqNumber: index + 1,
            })),
          },
        },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries(
              createRoutesInvalidationPredicate(),
            );
            toast.success("Temporary Route Successfully Created");
            reset();
            if (date) {
              navigate(generateRoute(AdRoutes.routePlanner, { date }));
            }
          },
          onError: () => {
            // Transfer failed, so the route is still empty - clean it up
            // (deleteVirtualRoute only succeeds while it has no locations).
            deleteVirtualRoute({ pathParams: { routeId: route.id } });
            setIsFinishing(false);
          },
        },
      );
    },
  });

  const driftErrorMessage =
    "The Route Planner has moved to a different day since you started " +
    "this flow. Please go back and start Create Temporary Route again.";

  const onCreateRoute = async () => {
    // Cheap first-pass check; can miss drift from a re-synced store (see fetch below).
    if (liveRoutePlannerDate && liveRoutePlannerDate !== storedDate) {
      toast.error(driftErrorMessage);
      return;
    }

    setIsFinishing(true);

    // Fresh check: confirm storedDate hasn't ended.
    try {
      const freshRoutes = await fetchAdminRouteV2ControllerGetRoutes({
        ...fetcherOptions,
        queryParams: { date: storedDate },
      });
      if (
        freshRoutes.data?.some((route) => route.state === RouteStatus.Ended)
      ) {
        toast.error(driftErrorMessage);
        setIsFinishing(false);
        return;
      }
    } catch {
      // If the check itself fails, fall through and let submission's own
      // error handling surface the problem.
    }

    handleSubmit();
  };

  return (
    <DashboardContentLayout>
      <Navbar showAccountMenu>
        <HeaderItems>
          <HeaderItemsLeft
            breadcrumbs={[
              {
                label: "Route Manager",
                url: date
                  ? generateRoute(AdRoutes.routePlanner, { date })
                  : undefined,
              },
              {
                label: "Create Temporary Route",
              },
            ]}
          />
          <HeaderItemsRight>
            <Button
              disabled={
                !values.routeName ||
                !values.color ||
                !values.driverId ||
                isCreatingRoute ||
                isFinishing
              }
              variant="contained"
              onClick={onCreateRoute}
              data-testid="create-temp-route-button"
            >
              Create Route
            </Button>
          </HeaderItemsRight>
        </HeaderItems>
      </Navbar>

      <CreateTempRouteWorkflowStepper />

      <Box sx={styles.content}>
        <Typography variant="h5">Create Temporary Route</Typography>
        <CreateTempRouteWarningBanner />

        <Box sx={styles.gridDetailContainer}>
          <Typography>Default Driver</Typography>
          <ControlledSelect
            id="driverId"
            name="driverId"
            placeholderSelectEnabled
            placeholder="Select Driver"
            options={driverOptions}
            value={values.driverId || ""}
            sx={styles.dropdownStyle}
            onBlur={handleBlur}
            onChange={(value) => {
              setFieldValue("driverId", value);
              setFieldTouched("driverId", true);
            }}
          />

          <Typography>Route Name</Typography>
          <TextField
            id="routeName"
            type="text"
            value={values.routeName}
            onChange={handleChange}
            onBlur={handleBlur}
            helperText={touched.routeName && errors.routeName}
            error={touched.routeName && !!errors.routeName}
            margin="dense"
            variant="outlined"
            inputProps={{ maxLength: 30 }}
            fullWidth
          />

          <Typography>Route Color</Typography>
          <CustomColorSelector
            id="color"
            placeholder="Select Color:"
            value={values.color}
            onBlur={handleBlur}
            onChange={(value) => {
              setFieldValue("color", value);
              setFieldTouched("color", true);
            }}
          />
        </Box>
      </Box>
    </DashboardContentLayout>
  );
};

const useStyles = makeSxStyles(() => ({
  content: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    marginTop: "24px",
    maxWidth: "740px",
  },
  gridDetailContainer: {
    mt: "16px",
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    rowGap: "24px",
    alignItems: "center",
  },
  dropdownStyle: {
    borderRadius: "4px",
    border: "1px solid #8B9198",
    height: "40px",

    "& > div.MuiSelect-select": {
      backgroundColor: "transparent !important",
    },
  },
}));

export default CreateTempRouteDetailsPage;
